import { isEmpty } from 'lodash'
import { createContext, Dispatch } from 'react'

import { ReducerAction, IReducerContextProps } from 'utility-tools/module/reducers'
import { TOverrideType } from 'utility-tools/module/types'
import {
    ECurrencyType,
    getPairCoinTypes,
    ICurrencyDto,
    TPriceRespApiDto,
} from 'nexchange-service/module/coins'

import { CoinInputType, ECoinInputType } from '../../../constants'

enum SelectCoinsReducerAction {
    SET_PAIR = 'SET_PAIR',
    SET_INPUT_LOADING = 'SET_INPUT_LOADING',
    CHANGE_FROM = 'CHANGE_FROM',
    CHANGE_TO = 'CHANGE_TO',
    LAST_CHANGED = 'LAST_CHANGED',
    CHANGE_COIN_LISTS = 'CHANGE_COIN_LISTS',
    BULK_CHANGE_STATE = 'BULK_CHANGE_STATE',
}

type TCurrencyDto = {
    code: string
    name: string
    isCrypto: boolean
    minimalAmount: number
    maximumAmount: number
}

type TCoinInputDto = {
    code: string
    isCrypto: boolean
    amount: number
    minAmount: number
    maxAmount: number
    isLoading?: boolean
}

type TCoinWidgetState = {
    coinListFrom: TCurrencyDto[]
    coinListTo: TCurrencyDto[]
    lastChanged: ECoinInputType

    from: TCoinInputDto
    to: TCoinInputDto
}

const defaultSelectedCoinsState: TCoinWidgetState = {
    from: {
        code: '', //'BTC',
        amount: 0,
        minAmount: 0,
        maxAmount: 0,
        isCrypto: true,
        isLoading: true,
    },
    to: {
        code: '', //'ETH',
        amount: 0,
        minAmount: 0,
        maxAmount: 0,
        isCrypto: true,
        isLoading: true,
    },

    coinListFrom: [],
    coinListTo: [],
    lastChanged: CoinInputType.FROM,
}

type TPairSetterDto = {
    availableCurrencies: ICurrencyDto[]
    from: TCoinInputDto
    to: TCoinInputDto
}

class SetSelectedPairAction extends ReducerAction<TPairSetterDto> {
    constructor(pairDto: TPairSetterDto) {
        super(SelectCoinsReducerAction.SET_PAIR, pairDto)
    }
}

type TInputLoadingActionPayload = {
    isFromCoinInputLoading?: boolean | null
    isToCoinInputLoading?: boolean | null
}
class SetInputLoadingStateAction extends ReducerAction<TInputLoadingActionPayload> {
    constructor({
        isFromCoinInputLoading = null,
        isToCoinInputLoading = null,
    }: TInputLoadingActionPayload) {
        super(SelectCoinsReducerAction.SET_INPUT_LOADING, {
            isFromCoinInputLoading,
            isToCoinInputLoading,
        })
    }
}

class SetCoinFromAction extends ReducerAction<Partial<TCoinInputDto>> {
    constructor(coinInputDto: Partial<TCoinInputDto>) {
        super(SelectCoinsReducerAction.CHANGE_FROM, coinInputDto)
    }
}

class SetCoinToAction extends ReducerAction<Partial<TCoinInputDto>> {
    constructor(coinInputDto: Partial<TCoinInputDto>) {
        super(SelectCoinsReducerAction.CHANGE_TO, coinInputDto)
    }
}

class SetLastCoinChangedAction extends ReducerAction<ECoinInputType> {
    constructor(coinInputType: ECoinInputType) {
        super(SelectCoinsReducerAction.LAST_CHANGED, coinInputType)
    }
}

type TSetCoinListsActionProps = { coinListFrom: object[]; coinListTo: object[] }
class SetCoinListsAction extends ReducerAction<TSetCoinListsActionProps> {
    constructor({ coinListFrom, coinListTo }: TSetCoinListsActionProps) {
        super(SelectCoinsReducerAction.CHANGE_COIN_LISTS, { coinListFrom, coinListTo })
    }
}

type TSelectedCoinsReducerAction =
    | SetSelectedPairAction
    | SetInputLoadingStateAction
    | SetCoinFromAction
    | SetCoinToAction
    | SetLastCoinChangedAction
    | SetCoinListsAction

const selectedCoinsReducer = (
    state: TCoinWidgetState = defaultSelectedCoinsState,
    action: TSelectedCoinsReducerAction,
): TCoinWidgetState => {
    const { type } = action

    switch (type) {
        case SelectCoinsReducerAction.SET_PAIR: {
            const { from, to, availableCurrencies } = action.payload as TPairSetterDto

            const isNewFromEmpty = isEmpty(from)
            const isNewToEmpty = isEmpty(to)

            const lastChanged = (() => {
                if (isNewFromEmpty && isNewToEmpty) {
                    return state.lastChanged
                }
                return isNewToEmpty ? CoinInputType.FROM : CoinInputType.TO
            })()

            const { fromCurrencyType, toCurrencyType } = getPairCoinTypes({
                availableCurrencies,
                fromCode: from.code,
                toCode: to.code,
            })

            return {
                ...state,
                from: isNewFromEmpty
                    ? state.from
                    : {
                          ...state.from,
                          ...from,
                          isCrypto: fromCurrencyType === ECurrencyType.CRYPTO,
                      },
                to: isNewToEmpty
                    ? state.to
                    : {
                          ...state.to,
                          ...to,
                          isCrypto: toCurrencyType === ECurrencyType.CRYPTO,
                      },
                lastChanged,
            }
        }

        case SelectCoinsReducerAction.SET_INPUT_LOADING: {
            const { isFromCoinInputLoading, isToCoinInputLoading } =
                action.payload as TInputLoadingActionPayload

            return <TCoinWidgetState>{
                ...state,
                from:
                    isFromCoinInputLoading === null
                        ? state.from
                        : {
                              ...state.from,
                              isLoading: isFromCoinInputLoading,
                          },
                to:
                    isToCoinInputLoading === null
                        ? state.to
                        : {
                              ...state.to,
                              isLoading: isToCoinInputLoading,
                          },
            }
        }

        case SelectCoinsReducerAction.CHANGE_FROM: {
            const { payload } = action as SetCoinFromAction

            return {
                ...state,
                from: {
                    ...state.from,
                    ...(payload as TPriceRespApiDto),
                },
            }
        }

        case SelectCoinsReducerAction.CHANGE_TO: {
            const { payload } = action as SetCoinToAction
            return {
                ...state,
                to: {
                    ...state.to,
                    ...(payload as TPriceRespApiDto),
                },
            }
        }

        case SelectCoinsReducerAction.LAST_CHANGED: {
            const { payload } = action as SetLastCoinChangedAction
            return <TCoinWidgetState>{
                ...state,
                lastChanged: payload,
            }
        }

        case SelectCoinsReducerAction.CHANGE_COIN_LISTS: {
            const { payload } = action as SetCoinListsAction
            // @ts-ignore
            const { coinListFrom, coinListTo } = payload
            return <TCoinWidgetState>{
                ...state,
                coinListFrom: coinListFrom || state.coinListFrom,
                coinListTo: coinListTo || state.coinListTo,
            }
        }

        case SelectCoinsReducerAction.BULK_CHANGE_STATE: {
            return <TCoinWidgetState>{
                ...state,
                // @ts-ignore
                ...action.payload,
            }
        }
        default:
            return state
    }
}

type TSelectedCoinsContext = TOverrideType<
    IReducerContextProps,
    {
        state: TCoinWidgetState
        dispatchAction?: Dispatch<TSelectedCoinsReducerAction>
    }
>

const SelectedCoinsContext = createContext<TSelectedCoinsContext>({
    state: defaultSelectedCoinsState,
})

export type { TCoinInputDto, TCoinWidgetState, TPairSetterDto }
export {
    selectedCoinsReducer,
    SelectedCoinsContext,
    defaultSelectedCoinsState,
    SelectCoinsReducerAction,
    SetSelectedPairAction,
    SetInputLoadingStateAction,
    SetCoinFromAction,
    SetCoinToAction,
    SetLastCoinChangedAction,
    SetCoinListsAction,
}
